/**
 * LocationsSection — Morgan Legal Group
 * Design: Navy background, borough cards with hover gold accent
 * Local SEO optimized with location-specific content
 */
import { useEffect, useRef, useState } from "react";
import { MapPin, ArrowRight } from "lucide-react";

const locations = [
  {
    name: "New York City",
    address: "15 Maiden Ln #905, New York, NY 10038",
    description: "Our flagship Manhattan office in the heart of the Financial District, steps from the New York Supreme Court.",
    href: "/nyc/",
    featured: true,
  },
  {
    name: "Brooklyn",
    address: "Serving all of Kings County",
    description: "Comprehensive estate planning and probate services for Brooklyn families, from Park Slope to Bay Ridge.",
    href: "/brooklyn/",
  },
  {
    name: "Long Island",
    address: "Nassau & Suffolk Counties",
    description: "Serving Nassau and Suffolk County residents with the same Manhattan-quality legal expertise.",
    href: "/long-island/",
  },
  {
    name: "The Bronx",
    address: "Serving all of Bronx County",
    description: "Dedicated legal services for Bronx families navigating estate planning and probate matters.",
    href: "/bronx/",
  },
  {
    name: "Queens",
    address: "Serving all of Queens County",
    description: "Estate planning attorneys serving Flushing, Forest Hills, Astoria, and all Queens neighborhoods.",
    href: "/queens/",
  },
  {
    name: "Staten Island",
    address: "Serving Richmond County",
    description: "Full-service estate planning and probate representation for Staten Island families.",
    href: "/staten-island/",
  },
];

export default function LocationsSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="locations" className="py-20 lg:py-28 bg-navy relative overflow-hidden">
      {/* Gold lines */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold to-transparent" />

      {/* Background watermark */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
        <span className="absolute -bottom-16 -right-8 font-display text-[16rem] font-bold text-white/[0.02] leading-none">
          NYC
        </span>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div ref={ref} className="max-w-2xl mb-14">
          <div className="section-label mb-4 text-gold">
            Our Locations
          </div>
          <h2 className="font-display text-white text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
            Serving All of{" "}
            <span className="text-gold italic">Greater New York</span>
          </h2>
          <p className="font-body text-white/60 text-base lg:text-lg leading-relaxed mt-4">
            With offices and service areas spanning all five boroughs and Long Island, Morgan Legal Group is always close to home when you need us most.
          </p>
        </div>

        {/* Locations grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {locations.map((loc, i) => (
            <a
              key={loc.name}
              href={loc.href}
              target="_blank"
              rel="noopener noreferrer"
              className={`group block p-6 lg:p-8 border transition-all duration-300 ${
                loc.featured
                  ? "border-gold bg-gold/10 hover:bg-gold/20"
                  : "border-white/10 bg-white/5 hover:bg-white/10 hover:border-gold/50"
              } ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
              style={{ transitionDelay: `${i * 80}ms`, transition: `opacity 600ms ${i * 80}ms, transform 600ms ${i * 80}ms, background-color 300ms, border-color 300ms` }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <MapPin className={`w-4 h-4 flex-shrink-0 ${loc.featured ? "text-gold" : "text-gold/70"}`} />
                  <h3 className={`font-display text-lg font-bold ${loc.featured ? "text-gold" : "text-white"}`}>
                    {loc.name}
                  </h3>
                </div>
                <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-gold transition-all duration-200 group-hover:translate-x-1" />
              </div>

              <p className="font-body text-white/50 text-xs mb-3">{loc.address}</p>
              <p className="font-body text-white/65 text-sm leading-relaxed">{loc.description}</p>

              {loc.featured && (
                <div className="mt-4 pt-4 border-t border-gold/30">
                  <span className="font-body text-gold text-xs font-semibold uppercase tracking-wide">Head Office</span>
                </div>
              )}
            </a>
          ))}
        </div>

        {/* Map placeholder / CTA */}
        <div
          className={`mt-10 p-6 lg:p-8 bg-white/5 border border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 transition-all duration-700 delay-500 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <div>
            <p className="font-display text-white text-xl font-bold mb-1">Head Office: 15 Maiden Lane, Suite 905</p>
            <p className="font-body text-white/60 text-sm">New York, NY 10038 · Financial District, Manhattan</p>
          </div>
          <div className="flex gap-3 flex-shrink-0">
            <a
              href="https://g.co/kgs/swwPdvP"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost-gold text-sm py-2.5 px-5"
            >
              Get Directions
            </a>
            <a href="tel:8885291315" className="btn-gold text-sm py-2.5 px-5">
              Call Now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
